import { useEffect, useRef, useCallback, useSyncExternalStore } from 'react';
import { useSocketIO } from '../providers/SocketIOProvider';
import type {
  PickingStartedEvent,
  PickingItemUpdatedEvent,
  PickingItemNotFoundEvent,
  PickingCompletedEvent,
  PickingProgressInfo,
} from '../providers/SocketIOProvider';

// ============================================================================
// Types
// ============================================================================

export interface OrderPickingState {
  orderId: string;
  orderNumber: string;
  picker: { id: string; name: string } | null;
  startedAt: string | null;
  progress: PickingProgressInfo | null;
  /** Per-line picking status keyed by lineId */
  lineStatuses: Map<string, LinePickingStatus>;
  hasNotFoundItems: boolean;
  completedAt: string | null;
}

export interface LinePickingStatus {
  lineId: string;
  productName: string;
  productSku: string;
  pickingStatus: 'found' | 'not_found';
  pickedQuantity: number;
  updatedAt: string;
}

// ============================================================================
// External Store (singleton shared across all hook consumers)
// ============================================================================

/** Map of orderId -> live picking state */
const pickingStateMap = new Map<string, OrderPickingState>();

/** Set of orderIds that have had not-found items */
const errorOrderIds = new Set<string>();

/** Subscribers for useSyncExternalStore */
let listeners = new Set<() => void>();
let snapshot = { pickingStateMap, errorOrderIds, version: 0 };

function emitChange() {
  snapshot = { pickingStateMap, errorOrderIds, version: snapshot.version + 1 };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

// ============================================================================
// State Mutation Helpers
// ============================================================================

function handlePickingStarted(event: PickingStartedEvent) {
  const existing = pickingStateMap.get(event.orderId);
  pickingStateMap.set(event.orderId, {
    orderId: event.orderId,
    orderNumber: event.orderNumber,
    picker: event.picker,
    startedAt: event.startedAt,
    progress: existing?.progress || null,
    lineStatuses: existing?.lineStatuses || new Map(),
    hasNotFoundItems: existing?.hasNotFoundItems || false,
    completedAt: null,
  });
  emitChange();
}

function handleItemUpdated(event: PickingItemUpdatedEvent) {
  const state = pickingStateMap.get(event.orderId) || {
    orderId: event.orderId,
    orderNumber: event.orderNumber,
    picker: null,
    startedAt: null,
    progress: null,
    lineStatuses: new Map<string, LinePickingStatus>(),
    hasNotFoundItems: false,
    completedAt: null,
  };

  state.progress = event.progress;
  state.lineStatuses.set(event.lineId, {
    lineId: event.lineId,
    productName: event.productName,
    productSku: event.productSku,
    pickingStatus: event.pickingStatus,
    pickedQuantity: event.pickedQuantity,
    updatedAt: event.timestamp,
  });

  if (event.pickingStatus === 'not_found') {
    state.hasNotFoundItems = true;
    errorOrderIds.add(event.orderId);
  }

  pickingStateMap.set(event.orderId, state);
  emitChange();
}

function handleItemNotFound(event: PickingItemNotFoundEvent) {
  const state = pickingStateMap.get(event.orderId);
  if (state) {
    state.hasNotFoundItems = true;
  }
  errorOrderIds.add(event.orderId);
  emitChange();
}

function handlePickingCompleted(event: PickingCompletedEvent) {
  const state = pickingStateMap.get(event.orderId);
  if (state) {
    state.progress = event.progress;
    state.hasNotFoundItems = event.hasNotFoundItems;
    state.completedAt = event.timestamp;
  }
  emitChange();
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Real-time picking status hook.
 *
 * Subscribes to Socket.IO picking events for the given store and maintains
 * a live Map of orderId → picking state. Multiple components can call this
 * hook with the same storeId — the singleton store ensures no duplication.
 *
 * @param storeId - The store's externalId to subscribe to
 */
export function usePickingStatus(storeId: string | null) {
  const {
    isConnected,
    subscribeStore,
    onPickingStarted,
    onPickingItemUpdated,
    onPickingItemNotFound,
    onPickingCompleted,
  } = useSocketIO();

  const storeIdRef = useRef(storeId);
  storeIdRef.current = storeId;

  // Subscribe to the store's admin room
  useEffect(() => {
    if (!storeId || !isConnected) return;

    // Join, never leave: the room is shared with the app-wide new-order alerts and live
    // order status, and the server's leave is not reference-counted — leaving on unmount
    // silenced that store's new-order alerts until the next reconnect.
    subscribeStore(storeId);
  }, [storeId, isConnected, subscribeStore]);

  // Register event listeners
  useEffect(() => {
    const unsubs = [
      onPickingStarted(handlePickingStarted),
      onPickingItemUpdated(handleItemUpdated),
      onPickingItemNotFound(handleItemNotFound),
      onPickingCompleted(handlePickingCompleted),
    ];

    return () => unsubs.forEach((u) => u());
  }, [onPickingStarted, onPickingItemUpdated, onPickingItemNotFound, onPickingCompleted]);

  const store = useSyncExternalStore(subscribe, getSnapshot);

  /** Get picking state for a specific order */
  const getOrderPickingState = useCallback(
    (orderId: string): OrderPickingState | undefined => {
      return store.pickingStateMap.get(orderId);
    },
    [store]
  );

  /** Check if an order has picking errors (not-found items) */
  const hasPickingError = useCallback(
    (orderId: string): boolean => {
      return store.errorOrderIds.has(orderId);
    },
    [store]
  );

  return {
    /** Full picking state map */
    pickingStateMap: store.pickingStateMap,
    /** Set of order IDs with not-found items */
    errorOrderIds: store.errorOrderIds,
    /** Get state for a specific order */
    getOrderPickingState,
    /** Check if order has picking errors */
    hasPickingError,
  };
}
